import * as Sentry from "@sentry/react";

import { wasReportedToSentry } from "./apiError";
import { deepScrub, redactText } from "./piiRedaction";
import { redactPii } from "./redact";

/**
 * Fallback DSN. Mantiene paridad con el `Sentry.init` previo en
 * `src/router/router.jsx` para no romper la captura existente cuando
 * el env var `REACT_APP_SENTRY_DSN` no está seteado todavía.
 */
const DEFAULT_DSN =
  "https://45c7136071640000b70caa54657e6007@o4506593438924800.ingest.sentry.io/4506713512869888";

let initialized = false;

/**
 * Inicializa Sentry React con configuración endurecida. Idempotente.
 *
 * Diseño conservador:
 *  - DSN desde `options.dsn` o `REACT_APP_SENTRY_DSN`, con fallback al hardcoded
 *    actual (sin breaking change). Setear el env var a `""` (string vacío) o
 *    pasar `options.dsn = ""` deshabilita Sentry sin tocar este archivo.
 *  - environment desde REACT_APP_SENTRY_ENVIRONMENT (cada .env.* define el suyo)
 *  - release desde REACT_APP_SENTRY_RELEASE (idealmente commit SHA en CI)
 *  - tracesSampler dinámico: respeta `parentSampled` (traces distribuidos),
 *    20% en hot paths (sendMessage/broadcast/conversation), 0 en healthchecks,
 *    5% baseline, 0 en `development`
 *  - replayIntegration con `replaysOnErrorSampleRate: 1.0`, `replaysSessionSampleRate: 0.05`,
 *    `maskAllText: true` y `blockAllMedia: true` para no exponer PII (teléfonos,
 *    nombres, mensajes) que el dashboard renderiza
 *  - tracePropagationTargets: backend + omnichannel
 *  - beforeSend con PII scrubbing (tokens en query string, Authorization, phone)
 *  - maxBreadcrumbs: 50
 *
 * Llamar desde `src/index.js` ANTES de `ReactDOM.render`.
 *
 * Idempotencia: el flag interno solo se setea tras un `Sentry.init` exitoso.
 * Si `Sentry.init` lanza, el flag queda en `false` y un siguiente llamado
 * puede reintentar.
 *
 * @param {Object} [options]
 * @param {import('history').History} [options.history] History de react-router para
 *   habilitar `reactRouterV5BrowserTracingIntegration`. Si no se provee, el tracing
 *   por navegación queda deshabilitado (los spans manuales y la captura siguen activos).
 * @param {string} [options.dsn] Override explícito del DSN. Si se pasa `""` o `null`,
 *   Sentry queda deshabilitado.
 * @returns {void}
 */
export function initSentry(options = {}) {
  if (initialized) return;

  // Resolución del DSN:
  //   - `options.dsn` toma precedencia (incluso si es `""`/`null` → disabled explícito)
  //   - luego `REACT_APP_SENTRY_DSN` (igualmente: `""` → disabled explícito)
  //   - finalmente fallback al hardcoded para no romper captura existente
  let dsn;
  if (Object.prototype.hasOwnProperty.call(options, "dsn")) {
    dsn = options.dsn;
  } else if (process.env.REACT_APP_SENTRY_DSN !== undefined) {
    dsn = process.env.REACT_APP_SENTRY_DSN;
  } else {
    dsn = DEFAULT_DSN;
  }
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] DSN empty — Sentry disabled");
    return;
  }

  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    process.env.REACT_APP_ENV ||
    process.env.NODE_ENV ||
    "development";

  const release = process.env.REACT_APP_SENTRY_RELEASE;

  const integrations = [];
  if (options.history && typeof Sentry.reactRouterV5BrowserTracingIntegration === "function") {
    integrations.push(
      Sentry.reactRouterV5BrowserTracingIntegration({ history: options.history })
    );
  }
  // Replay: 100% en error, 5% sesiones random. Importante para investigar
  // sesiones de agentes que reporten bugs sin necesidad de pedirles repro.
  if (typeof Sentry.replayIntegration === "function") {
    integrations.push(
      Sentry.replayIntegration({
        // Mask por defecto: el dashboard renderiza PII (teléfonos, nombres,
        // mensajes). El scrubbing de `beforeSend` NO aplica al payload de Replay.
        // Si se necesita unmasking selectivo en vistas seguras, marcar nodos con
        // la clase `sentry-unmask` (ver docs de Replay).
        maskAllText: true,
        blockAllMedia: true,
      })
    );
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      integrations,
      tracesSampler: makeSampler(environment),
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      // Patrones que reciben los headers `sentry-trace` y `baggage` para
      // continuación de traza distribuida. Cubrir TODOS los hosts que el
      // dashboard golpea para que cualquier llamada quede enlazada al
      // backend en Sentry:
      //   - graph[*].iventas        → GraphQL (graph.iventas.com,
      //                                gabograph.iventas.mx, arturograph,
      //                                danograph.iventas.mx)
      //   - restservices.iventas    → REST principal (src/axios.js)
      //   - <user>restservices.iventas.mx → REST por dev (arturo/gabo/dano)
      //   - payments[*].iventas     → REST payments (src/axiosPayments.js)
      //                                (payments.iventas.mx,
      //                                payments-development.iventas.mx,
      //                                payments-dano.iventas.mx)
      //   - boards[*].iventas       → boards API + Pusher auth endpoint
      //   - iv-analytics-service*.herokuapp.com (legacy, Heroku) y
      //     iventas-analytics*.up.railway.app (Railway) → microservicio de analytics
      //                                (REACT_APP_ANALYTICS_API_URL,
      //                                src/services/analyticsDashboard.service.js).
      //                                Ahora que ese servicio valida el JWT y
      //                                hace tenant-scoping (ver PR de backend
      //                                iVentas-Plus/iventas-analytics#1), se
      //                                propaga sentry-trace/baggage para poder
      //                                correlacionar la request del dashboard
      //                                con los spans del backend de analytics.
      //   - localhost:3002/3006     → dev local del backend
      //   - api.iventas / omnichannel → reservados para futuros endpoints
      // Si se agrega un endpoint nuevo (p.ej. analytics.iventas), añadirlo
      // aquí para no perder propagación.
      tracePropagationTargets: [
        /graph.*\.iventas/i,
        /restservices\.iventas/i,
        /payments.*\.iventas/i,
        /boards.*\.iventas/i,
        /^https:\/\/iv-analytics-service[a-z0-9-]*\.herokuapp\.com(?:[/?#]|$)/i,
        /^https:\/\/iventas-analytics[a-z0-9-]*\.up\.railway\.app(?:[/?#]|$)/i,
        /localhost:300[26]/i,
        /api\.iventas/i,
        /omnichannel/i,
      ],
      normalizeDepth: 5,
      maxBreadcrumbs: 50,
      sendDefaultPii: false,
      beforeSend: scrubPii,
      beforeSendTransaction: scrubPii,
      ignoreErrors: [
        // Ruido conocido de browsers/extensions/redes
        /ResizeObserver loop limit/,
        /ResizeObserver loop completed with undelivered notifications/,
        /Non-Error promise rejection captured/,
        /Network request failed/,
        /Failed to fetch/,
        /Load failed/,
      ],
    });
  } catch (e) {
    // No bloquees retries futuros si init falló. No relanzar — Sentry roto
    // no debe tirar la app.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed:", e && e.message);
    return;
  }

  initialized = true;
  // Tag global para queries cross-proyecto en Sentry (backend, mobile, dashboard).
  Sentry.setTag("service", "dashboard");
}

/**
 * Sampler dinámico. Política real implementada (mantener docstring sincronizado
 * con el cuerpo):
 *  - `environment === 'development'` → 0 (no trazamos en dev local).
 *  - `parentSampled` definido → se respeta (1 si parent sampled, 0 si no);
 *    mantiene traces distribuidos consistentes con el backend.
 *  - `name` matches `/health|ping|metrics/i` → 0 (sin valor diagnóstico).
 *  - `name` matches `/sendMessage|broadcast|conversation/i` → 0.2 (hot paths).
 *  - default → 0.05 (baseline para prod/staging).
 *
 * Nota: este sampler es para `transactions/traces`. La captura de errores
 * (`captureException`/`captureMessage`) NO depende de él — los errores siempre
 * se envían (sujetos a `ignoreErrors` y `beforeSend`).
 *
 * @param {string} environment
 * @returns {(ctx: import('@sentry/react').SamplingContext) => number}
 */
function makeSampler(environment) {
  return (ctx) => {
    if (environment === "development") return 0;

    // Si el cliente continúa una traza distribuida, respetar parentSampled
    if (ctx && ctx.parentSampled !== undefined && ctx.parentSampled !== null) {
      return ctx.parentSampled ? 1 : 0;
    }

    const name = (ctx && ctx.name) || "";

    // Healthchecks o navegaciones a /health → 0
    if (/health|ping|metrics/i.test(name)) return 0;

    // Hot paths críticos
    if (/sendMessage|broadcast|conversation/i.test(name)) return 0.2;

    return 0.05;
  };
}

/**
 * Nombres de adjunto que llevan un volcado de estado de la aplicación.
 * `redux_state.json` lo genera `Sentry.createReduxEnhancer` cuando
 * `attachReduxState` queda en su valor por defecto (`true`).
 *
 * Por qué se filtran aquí además de apagarlos en origen: los adjuntos NO
 * pasan por el data scrubbing de Sentry salvo que el proyecto active
 * "additional scrubbing rules on attachments", así que un adjunto es la vía
 * que se salta TODAS las demás defensas. Si alguien vuelve a encender
 * `attachReduxState`, esto lo detiene igual.
 */
const STATE_ATTACHMENT_FILENAMES = /(^|_)state\.json$/i;

/**
 * ¿Este evento es la copia que produjo el handler global (`onerror` /
 * `onunhandledrejection`) de un error que `apiError()` YA reportó?
 *
 * Se exige que se cumplan las dos cosas:
 *  1. la excepción original trae la marca de `reportApiError`, y
 *  2. el evento viene de un handler global (`mechanism.handled === false`).
 *
 * La condición (2) es la que evita descartar nuestro propio evento, que es
 * `handled: true`. No dependemos del ORDEN en que ocurren las capturas.
 *
 * Solo aplica a eventos de error: las transactions (`event.type ===
 * 'transaction'`) comparten este `beforeSend` y nunca deben descartarse aquí.
 *
 * @param {import('@sentry/react').Event} event
 * @param {import('@sentry/react').EventHint} [hint]
 * @returns {boolean}
 */
function isUnhandledDuplicate(event, hint) {
  if (!event || event.type) return false; // transactions y perfiles: fuera
  if (!hint || !wasReportedToSentry(hint.originalException)) return false;

  const values =
    event.exception && Array.isArray(event.exception.values)
      ? event.exception.values
      : [];
  return values.some(
    (v) => v && v.mechanism && v.mechanism.handled === false
  );
}

/**
 * Redacta PII del texto de las excepciones del evento.
 *
 * Importante para este fix: al encadenar la causa original, Sentry adjunta
 * la excepción del backend como exception ligada (`linkedErrorsIntegration`
 * viene activa por default y sigue la propiedad `cause`). Ese texto puede
 * traer teléfonos o correos de clientes. Aquí se limpia.
 *
 * POR QUÉ SOBREVIVE AL REBASE SOBRE EL #230 (y no se sustituye por su
 * `redactText`): el encabezado de `redact.js` mandaba borrar este módulo por
 * considerarlo un duplicado peor. Se midió ejecutando los dos redactores sobre
 * la misma batería y es FALSO en tres casos, todos dentro de la superficie que
 * abre justamente este PR — texto de error del backend:
 *
 *   - `Authorization: Bearer eyJ…`  → `redactText` lo deja ENTERO.
 *   - `password: hunter2` / `{"api_key":"sk_live_…"}` fuera de un query string
 *     → `redactText` lo deja ENTERO (su `SECRET_PARAM_RE` exige `[?&#]…=`).
 *   - `5551234567x89` (teléfono con extensión) → `redactText` lo deja ENTERO.
 *
 * Los dos redactores se aplican en cadena a propósito: éste primero, sobre el
 * texto libre no confiable, y después el barrido del #230 sobre todo el evento.
 * Ninguno de los marcadores (`[email]`, `[phone]`, `[token]`, `[Filtered]`)
 * vuelve a casar con los patrones del otro, así que encadenarlos no degrada el
 * texto. La unificación de ambos redactores es deuda ya registrada en #313.
 *
 * @param {import('@sentry/react').Event} event
 * @returns {void}
 */
function redactExceptionValues(event) {
  if (typeof event.message === "string") {
    event.message = redactPii(event.message);
  }
  const values =
    event.exception && Array.isArray(event.exception.values)
      ? event.exception.values
      : [];
  values.forEach((v) => {
    if (v && typeof v.value === "string") {
      v.value = redactPii(v.value);
    }
  });
}

/**
 * Limpia campos PII de eventos antes de enviar a Sentry. Defensivo: si
 * la forma del evento no coincide con lo esperado, retorna sin cambios.
 *
 * Importante: el dashboard puede recibir tokens en query strings (magic links,
 * callbacks OAuth, parámetros con `token=`/`api_key=`/`password=`/etc).
 * `scrubPii` cubre `request.query_string` independientemente de qué
 * integraciones default de `@sentry/browser` estén activas — no asumimos
 * `requestDataIntegration` con `query_string: false`.
 *
 * CAPA 2 del arreglo de fuga de PII: además de los campos puntuales de
 * `request`, hace un barrido recursivo de `contexts`, `breadcrumbs`, `extra`
 * y `tags`, y redacta el título del evento. Ese barrido es la red que atrapa
 * lo que la capa 1 (`observability/reduxSentry.js`) no anticipó.
 *
 * NOTA deliberada: `event.user` NO se toca. Lleva el correo del ASESOR
 * (`Sentry.setUser` en `redux/userDuck.js`), que no es un cliente final y es
 * lo que permite saber a quién le pasó el error. Quitarlo es una decisión de
 * producto, no técnica.
 *
 * @param {import('@sentry/react').Event | null | undefined} event
 * @param {import('@sentry/react').EventHint} [hint] Necesario para poder
 *   descartar adjuntos; el SDK siempre lo pasa como segundo argumento.
 * @returns {import('@sentry/react').Event | null}
 */
function scrubPii(event, hint) {
  if (!event) return null;

  // Dedup de errores de API ya reportados por `apiError()`.
  //
  // Varios callers despachan estas acciones sin `.catch()` (p.ej.
  // `dispatch(searchCategoriesAction())` en `AgentsView.jsx`), así que la
  // promesa rechazada llega al handler global de Sentry y genera un SEGUNDO
  // evento del mismo fallo — sin tags ni fingerprint, y quemando cuota.
  //
  // Descartamos solo la copia del handler global: `mechanism.handled === false`
  // identifica `onerror` / `onunhandledrejection`. El evento explícito de
  // `reportApiError` es `handled: true` y sobrevive, que es el que trae la
  // causa, los tags de operación y el fingerprint consolidado.
  try {
    if (isUnhandledDuplicate(event, hint)) return null;
  } catch (_e) {
    // Ante la duda, preferimos enviar un evento duplicado a perderlo.
  }

  try {
    // El texto de la causa preservada viene del backend y NO es confiable:
    // hay precedente de teléfonos de clientes embebidos en errores de Prisma
    // que acabaron indexados en Sentry. Ver `observability/redact.js`.
    redactExceptionValues(event);
    // request.data: phone, token, body, password
    if (event.request && event.request.data && typeof event.request.data === "object") {
      const data = event.request.data;
      [
        "phone",
        "to",
        "from",
        "messageBody",
        "body",
        "text",
        "token",
        "password",
        "apiKey",
        "api_key",
      ].forEach((k) => {
        if (k in data) delete data[k];
      });
      // Y encima, el barrido recursivo. El borrado plano de arriba solo mira
      // claves de PRIMER nivel, así que un teléfono en `variables.userId` de un
      // POST de GraphQL —el caso hermano DASHBOARD-ZC-KQ / #231— salía intacto
      // si `requestDataIntegration` llegaba a adjuntar el body. Lo pidieron
      // @arturogaro (B2, bloqueante), @diegosiac y @kirtashGuerrero27.
      // El patrón por VALOR lo atrapa aunque la clave no se llame `phone`.
      event.request.data = deepScrub(event.request.data);
    } else if (event.request && typeof event.request.data === "string") {
      // El body serializado (GraphQL manda JSON como string) también es texto
      // libre con teléfonos dentro.
      event.request.data = redactText(event.request.data);
    }
    // request.url / Referer: el SDK de navegador copia `location.href` aquí
    // (`httpContextIntegration`), y el magic-link lleva el JWT de sesión en el
    // query string. Ver `SECRET_PARAM_RE` en `piiRedaction.js`.
    if (event.request && typeof event.request.url === "string") {
      event.request.url = redactText(event.request.url);
    }
    // request.headers (case-insensitive)
    if (event.request && event.request.headers && typeof event.request.headers === "object") {
      const PII = new Set(["authorization", "cookie", "x-api-key", "x-auth-token"]);
      Object.keys(event.request.headers).forEach((k) => {
        if (PII.has(k.toLowerCase())) delete event.request.headers[k];
      });
      // El `Referer` lo pone el mismo `httpContextIntegration` y trae la URL
      // ANTERIOR — o sea, el magic-link con el token si el asesor acaba de
      // entrar por ahí. Mismo tratamiento que `request.url`.
      if (typeof event.request.headers.Referer === "string") {
        event.request.headers.Referer = redactText(event.request.headers.Referer);
      }
    }
    // request.query_string: en el navegador este campo NO lo setea nadie
    // (`httpContextIntegration` de `@sentry/browser` 8.55 solo pone `url`,
    // `Referer` y `User-Agent`; `query_string` es de `@sentry/node`). Se deja
    // porque cuesta cero y cubre SSR o un `captureEvent` a mano, pero la
    // protección REAL de este canal es la redacción de `request.url` de arriba.
    // El boundary `(^|[?&])` cubre el primer parámetro cuando el string viene
    // sin `?` inicial (p.ej. `token=xxx&x=y`). Mantenemos el separador en $1
    // para que el query string siga siendo válido tras el scrub.
    if (event.request && typeof event.request.query_string === "string") {
      event.request.query_string = event.request.query_string.replace(
        /(^|[?&])((?:token|access_token|api_key|apiKey|password)=)[^&]*/gi,
        "$1$2[Filtered]"
      );
    }
    // extra: por si algún captureException acompañó context con tokens
    if (event.extra && typeof event.extra === "object") {
      ["token", "password", "apiKey", "api_key", "authorization"].forEach((k) => {
        if (k in event.extra) delete event.extra[k];
      });
    }

    // --- CAPA 2: barrido recursivo ---
    // Aquí es donde se detiene el volcado de estado de Redux
    // (`contexts.state`) y el payload de las acciones (`breadcrumbs`), que es
    // por donde se estaba yendo la bandeja del asesor.
    if (event.contexts && typeof event.contexts === "object") {
      // Único sitio con la exención de `TECHNICAL_ROOTS`: aquí `device`, `os` y
      // `browser` son los contextos del SDK. En el resto de destinos —y sobre
      // todo en `request.data`— una clave con ese nombre es dato de cliente.
      event.contexts = deepScrub(event.contexts, { technicalRoots: true });
    }
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = deepScrub(event.breadcrumbs);
    }
    if (event.extra && typeof event.extra === "object") {
      event.extra = deepScrub(event.extra);
    }
    if (event.tags && typeof event.tags === "object") {
      event.tags = deepScrub(event.tags);
    }

    // Título del evento: es lo que Sentry indexa y muestra en la lista de
    // issues. Un teléfono ahí queda visible sin abrir el evento, y además
    // fragmenta un mismo bug en un issue por cliente.
    if (typeof event.message === "string") {
      event.message = redactText(event.message);
    }
    if (event.logentry && typeof event.logentry.message === "string") {
      event.logentry.message = redactText(event.logentry.message);
    }
    if (event.exception && Array.isArray(event.exception.values)) {
      event.exception.values.forEach((v) => {
        if (v && typeof v.value === "string") v.value = redactText(v.value);
      });
    }

    // Adjuntos: no pasan por el scrubbing de Sentry. Se descartan los
    // volcados de estado aunque alguien reactive `attachReduxState`.
    if (hint && Array.isArray(hint.attachments) && hint.attachments.length > 0) {
      hint.attachments = hint.attachments.filter(
        (a) => !(a && typeof a.filename === "string" && STATE_ATTACHMENT_FILENAMES.test(a.filename))
      );
    }
  } catch (e) {
    // Nunca dejes que scrubbing tire el evento entero — preferimos enviar el
    // evento posiblemente con PII parcial a perderlo. El catch sale al return
    // de abajo manteniendo el `event` (posiblemente parcialmente mutado).
    // eslint-disable-next-line no-console
    console.warn(
      "[sentry] scrubPii threw, returning event without further scrubbing:",
      e && e.message
    );
  }
  return event;
}

export { scrubPii };
